import { CoverageState } from "../domain/types";

interface TranscriptWord {
  start?: number;
  end?: number;
}

interface TranscriptPayload {
  is_partial?: boolean;
  data?: {
    text?: string;
    words?: TranscriptWord[];
    audio_duration?: number;
  };
}

const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s]+/g, " ")
    .trim();

export class CoverageTracker {
  private hardCommittedMs = 0;
  private softCommittedMs = 0;
  private lastFinalCoverageMs = 0;
  private lastStablePartialCoverageMs = 0;
  private lastCommitAdvanceAt: number | null = null;

  private lastPartialNormalized = "";
  private lastPartialCoverageMs = 0;
  private partialStreakCount = 0;
  private partialStreakStartedAt: number | null = null;

  constructor(
    private readonly options: {
      partialStabilityMinCount: number;
      partialStabilityMinMs: number;
    }
  ) {}

  onTranscriptMessage(input: unknown, now = Date.now()): CoverageState {
    const payload = input as TranscriptPayload;
    const words = payload?.data?.words;
    const audioDurationRaw = payload?.data?.audio_duration;

    if (!Array.isArray(words) || typeof audioDurationRaw !== "number") {
      return this.snapshot(false);
    }

    const audioDurationMs = Math.max(audioDurationRaw * 1000, 0);
    const lastWordEndMs = this.extractLastWordEndMs(words);

    if (lastWordEndMs === null) {
      return this.snapshot(false);
    }

    if (payload.is_partial) {
      const partialCoverageMs = Math.min(audioDurationMs, lastWordEndMs + 250);
      this.trackPartialStability(payload?.data?.text ?? "", partialCoverageMs, now);
      const stablePartialCoverageMs = this.computeStablePartialCoverage(now);

      if (stablePartialCoverageMs !== null) {
        this.lastStablePartialCoverageMs = Math.max(
          this.lastStablePartialCoverageMs,
          stablePartialCoverageMs
        );
        const nextSoftCommit = Math.max(stablePartialCoverageMs - 500, 0);

        if (nextSoftCommit > this.softCommittedMs) {
          this.softCommittedMs = nextSoftCommit;
          this.lastCommitAdvanceAt = now;
          return this.snapshot(true);
        }
      }

      return this.snapshot(false);
    }

    const finalCoverageMs = Math.min(audioDurationMs, lastWordEndMs + 500);
    this.lastFinalCoverageMs = Math.max(this.lastFinalCoverageMs, finalCoverageMs);
    const nextHardCommit = Math.max(finalCoverageMs - 250, 0);

    if (nextHardCommit > this.hardCommittedMs) {
      this.hardCommittedMs = nextHardCommit;
      this.lastCommitAdvanceAt = now;
      return this.snapshot(true);
    }

    return this.snapshot(false);
  }

  getState(): CoverageState {
    return this.snapshot(false);
  }

  private snapshot(updated: boolean): CoverageState {
    return {
      hardCommittedMs: this.hardCommittedMs,
      softCommittedMs: this.softCommittedMs,
      lastFinalCoverageMs: this.lastFinalCoverageMs,
      lastStablePartialCoverageMs: this.lastStablePartialCoverageMs,
      lastCommitAdvanceAt: this.lastCommitAdvanceAt,
      updated,
    };
  }

  private extractLastWordEndMs(words: TranscriptWord[]): number | null {
    let lastWordEndMs: number | null = null;

    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      if (typeof word?.end !== "number") {
        continue;
      }

      const candidate = Math.max(word.end * 1000, 0);
      if (lastWordEndMs === null || candidate > lastWordEndMs) {
        lastWordEndMs = candidate;
      }
    }

    return lastWordEndMs;
  }

  private trackPartialStability(text: string, coverageMs: number, now: number) {
    const normalized = normalizeText(text);

    if (!normalized) {
      this.lastPartialNormalized = "";
      this.partialStreakCount = 0;
      this.partialStreakStartedAt = null;
      this.lastPartialCoverageMs = coverageMs;
      return;
    }

    if (normalized === this.lastPartialNormalized) {
      this.partialStreakCount += 1;
    } else {
      this.lastPartialNormalized = normalized;
      this.partialStreakCount = 1;
      this.partialStreakStartedAt = now;
    }

    this.lastPartialCoverageMs = coverageMs;
  }

  private computeStablePartialCoverage(now: number): number | null {
    if (!this.lastPartialNormalized || this.partialStreakCount <= 0) {
      return null;
    }

    const streakDuration =
      this.partialStreakStartedAt === null
        ? 0
        : Math.max(now - this.partialStreakStartedAt, 0);

    if (
      this.partialStreakCount >= this.options.partialStabilityMinCount ||
      streakDuration >= this.options.partialStabilityMinMs
    ) {
      return this.lastPartialCoverageMs;
    }

    return null;
  }
}
